import React from "react";
import { motion } from "framer-motion";
import {
  Crosshair,
  MapPin,
  LineChart,
  Hourglass,
  Layers,
  Monitor,
} from "lucide-react";

const features = [
  {
    icon: Crosshair,
    title: "Precision Targeting",
    body:
      "Reach UHNI and HNI audiences directly inside the rooms, fairways and lounges they already inhabit.",
  },
  {
    icon: MapPin,
    title: "Strategic Locations",
    body:
      "Curated placements across India's elite golf clubs and private members' venues.",
  },
  {
    icon: LineChart,
    title: "Real-Time Analytics",
    body:
      "Granular, dashboard-grade insights into audience engagement and campaign performance.",
  },
  {
    icon: Hourglass,
    title: "Extended Dwell Time",
    body:
      "An average 30+ minute exposure window — the rarest commodity in modern advertising.",
  },
  {
    icon: Layers,
    title: "Programmatic Flexibility",
    body:
      "Real-time creative updates, dayparting and dynamic scheduling, fully programmatic.",
  },
  {
    icon: Monitor,
    title: "World-Class Installations",
    body:
      "Globally specified digital displays, engineered for clarity, reliability and longevity.",
  },
];

const ease = [0.22, 1, 0.36, 1];

const WhyChooseUs = () => {
  return (
    <section id="why" data-testid="why-section" className="relative py-28 md:py-40 bg-ivory-soft">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-24 items-end">
          <div className="md:col-span-7">
            <span className="eyebrow flex items-center gap-3">
              <span className="w-10 h-px bg-brass" />
              Why Green Signs
            </span>
            <h2 className="h-display text-4xl md:text-6xl mt-6">
              Premium <span className="italic text-brass font-medium">PrDOOH</span>, designed to connect
              <br />
              <span className="italic text-brass">India's most affluent</span> audiences.
            </h2>
          </div>
          <div className="md:col-span-5 md:pl-8 md:border-l md:border-forest-800/15">
            <p className="text-forest-600 text-base md:text-lg leading-relaxed font-light">
              Six considered reasons our partners choose us — every one of them
              measurable, every one of them earned.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.article
                key={f.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, ease, delay: i * 0.05 }}
                className="hairline shadow-luxe-hover hairline-hover bg-ivory p-8 md:p-10 group"
                data-testid={`feature-card-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="w-12 h-12 hairline flex items-center justify-center text-brass group-hover:bg-brass/5 transition-colors duration-500">
                    <Icon size={20} strokeWidth={1.5} />
                  </span>
                  <span className="font-serif italic text-brass/40 text-2xl">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-serif text-2xl md:text-[28px] text-forest-900 mt-10 whitespace-nowrap">
                  {f.title}
                </h3>
                <p className="mt-4 text-forest-600 text-sm md:text-base leading-relaxed font-light">
                  {f.body}
                </p>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
